import { useOnboarding, guideSteps } from "hooks/useOnboarding";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "store/hooks";

const handleNavigation = async ({
  navigate,
  path,
  parentMenuSelector = undefined,
  menuSelector,
}) => {
  navigate(path);
  const parentMenu = parentMenuSelector
    ? document.querySelector(parentMenuSelector)
    : undefined;
  const menu = document.querySelector(menuSelector);
  if (!menu) {
    parentMenu?.click();
  }
  return new Promise((resolve) => {
    setTimeout(() => {
      if (document.querySelector(menuSelector)) {
        resolve();
      }
    }, 100);
  });
};

const getInfo = ({ navigate }) => ({
  [guideSteps.PROFILE]: {
    content: [
      {
        title: "Dashboard Tutorial",
        description:
          "Get to know your dashboard in just a few clicks! Let’s walk you through the essentials.",
      },
    ],
    nextCallback: () =>
      handleNavigation({
        navigate,
        path: "/my_organization_details",
        parentMenuSelector: '[data-id="My Organization"]',
        menuSelector: `.${guideSteps.ORGANIZATION_PROFILE}`,
      }),
  },
  [guideSteps.ORGANIZATION_PROFILE]: {
    content: [
      {
        title: "Personalize your Organization's profile",
        description:
          "Complete your Organizations Profile and upload your organization logo to customize your Digital Button's appearance.",
      },
    ],
    prevCallback: () =>
      handleNavigation({
        navigate,
        path: "/",
        menuSelector: `.${guideSteps.PROFILE}`,
      }),
    nextCallback: () =>
      handleNavigation({
        navigate,
        path: "/users",
        parentMenuSelector: '[data-id="My Organization"]',
        menuSelector: `.${guideSteps.USERS}`,
      }),
  },
  [guideSteps.USERS]: {
    content: [
      {
        title: "Add a User",
        description:
          "Invite your team members to collaborate on Referral Stream.",
      },
    ],
    prevCallback: () =>
      handleNavigation({
        navigate,
        path: "/my_organization_details",
        parentMenuSelector: '[data-id="My Organization"]',
        menuSelector: `.${guideSteps.ORGANIZATION_PROFILE}`,
      }),
    nextCallback: () =>
      handleNavigation({
        navigate,
        path: "/payment_method",
        parentMenuSelector: '[data-id="My Organization"]',
        menuSelector: `.${guideSteps.PAYMENT}`,
      }),
  },
  [guideSteps.PAYMENT]: {
    content: [
      {
        title: "Add Payment Method",
        description: "Add and maintain payment details.",
      },
    ],
    prevCallback: () =>
      handleNavigation({
        navigate,
        path: "/users",
        parentMenuSelector: '[data-id="My Organization"]',
        menuSelector: `.${guideSteps.USERS}`,
      }),
    nextCallback: () =>
      handleNavigation({
        navigate,
        path: "/practices",
        menuSelector: `.${guideSteps.PAYMENT}`,
      }),
  },
  [guideSteps.PRACTICES]: {
    content: [
      {
        title: "Add your Referring Practices",
        description:
          "Add a physical location, like an office, surgery center or clinic, where referral senders can refer patients to you.",
      },
    ],
    prevCallback: () =>
      handleNavigation({
        navigate,
        path: "/payment_method",
        parentMenuSelector: '[data-id="My Organization"]',
        menuSelector: `.${guideSteps.PAYMENT}`,
      }),
    nextCallback: () =>
      handleNavigation({
        navigate,
        path: "/senders",
        menuSelector: `.${guideSteps.SENDERS}`,
      }),
  },
  [guideSteps.SENDERS]: {
    content: [
      {
        title: "Add Referral Senders",
        description:
          "Senders work at a Referring Practice. They click the physical and digital buttons to refer patients to you. Example of a Sender is a referral coordinator, front desk receptionist, etc.",
      },
    ],
    prevCallback: () =>
      handleNavigation({
        navigate,
        path: "/practices",
        menuSelector: `.${guideSteps.PRACTICES}`,
      }),
    nextCallback: () =>
      handleNavigation({
        navigate,
        path: "/button_locations",
        menuSelector: `.${guideSteps.BUTTONS}`,
      }),
  },
  [guideSteps.BUTTONS]: {
    content: [
      {
        title: "Add a Button",
        description: (
          <>
            Add a Digital or Physical button and assign it to a Referral Sender.
            <div className="mt-2">
              <b>Digital buttons:</b> Create as many as you want for all your
              Senders. Can be accessed in web browser or app.
            </div>
            <div className="mt-2">
              <b>Physical buttons:</b> To be placed strategically in your
              Sender's offices. Senders physically press the button to refer.
            </div>
          </>
        ),
      },
    ],
    prevCallback: () =>
      handleNavigation({
        navigate,
        path: "/senders",
        menuSelector: `.${guideSteps.SENDERS}`,
      }),
    nextCallback: () =>
      handleNavigation({
        navigate,
        path: "/analytics",
        menuSelector: `.${guideSteps.ANALYTICS}`,
      }),
  },
  [guideSteps.ANALYTICS]: {
    content: [
      {
        title: "Track your Analytics",
        description:
          "Track your Referrals, Questions and Notifications in real-time",
      },
    ],
    prevCallback: () =>
      handleNavigation({
        navigate,
        path: "/button_locations",
        menuSelector: `.${guideSteps.BUTTONS}`,
      }),
    nextCallback: () => navigate("/"),
  },
});

export const Onboarding = () => {
  const navigate = useNavigate();
  const spinner = useAppSelector((state) => state.spinner);

  const {
    isOnboardingFinished,
    currentStep,
    nextStep,
    prevStep,
    finishOnboarding,
    onboardingTarget,
    posLeft,
    posTop,
    currentStepIndex,
    stepsLength,
  } = useOnboarding();

  const info = useMemo(() => getInfo({ navigate }), [navigate]);

  if (isOnboardingFinished || !onboardingTarget) {
    return null;
  }

  return (
    <>
      <div
        id="onboarding-marker"
        className="absolute bg-white rounded-xl shadow-2xl mr-4 border-solid border-gray-200 border-1 max-w-96"
        style={{ top: posTop, left: posLeft, zIndex: 60 }}
      >
        <div className="relative pt-4 pb-4 pl-6 pr-6 text-sm">
          <div className="absolute -top-3 -left-3 bg-blue-600 w-6 h-6 rounded-full"></div>
          <div
            onClick={finishOnboarding}
            className="absolute top-2 right-2 hover:bg-gray-100 w-6 h-6 cursor-pointer rounded-full"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="size-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18 18 6M6 6l12 12"
              />
            </svg>
          </div>
          {info[currentStep].content.map(({ title, description }, index) => (
            <React.Fragment key={`${index}-${title}`}>
              <div className="font-bold text-lg">{title}</div>
              <div className="mb-2 text-md">{description}</div>
            </React.Fragment>
          ))}
          <div className="flex flex-row justify-between items-end gap-5">
            <div className="text-md font-semibold text-gray-500">
              {currentStepIndex} of {stepsLength}
            </div>
            <div className="flex flex-row gap-2">
              {currentStep !== Object.keys(guideSteps)[0] && (
                <button
                  onClick={() => {
                    if (typeof info[currentStep]?.prevCallback === "function") {
                      info[currentStep].prevCallback();
                    } 
                    prevStep(); 
                  }}
                  className="bg-gray-200 hover:bg-gray-100 text-gray-600 font-bold rounded-lg px-4 py-2 mt-4 disabled:opacity-70"
                  disabled={!!spinner}
                >
                  Previous step
                </button>
              )}
              <button
                onClick={async () => {
                  if (typeof info[currentStep]?.nextCallback === "function") {
                    await info[currentStep].nextCallback();
                  }
                  nextStep();
                }}
                className="bg-black hover:bg-gray-800 text-white font-bold rounded-lg px-4 py-2 mt-4 disabled:opacity-70"
                disabled={!!spinner}
              >
                {currentStepIndex === stepsLength ? "Complete" : "Next"}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        className="fixed top-0 bottom-0 left-0 right-0 bg-black opacity-15"
        style={{ zIndex: 55 }}
      ></div>
    </>
  );
};
